// Pure booking-form validation carved out of controllers/index.js's
// confirmBookingTask. The functions build a `requiredEmpty` map and an error
// list from explicit inputs; the controller stays responsible for the async
// request and for publishing the results onto tracked state.

import moment from 'moment-timezone';
import { set, get } from '@ember/object';
import { clearAttribute } from 'beebee-base/lib/utility';

function validateEmail(email) {
  var re = /\S+@\S+\.\S+/;
  return re.test(email);
}

// Flags every GDPR term option that is required for registration but not
// selected. Mutates `option.error` in place, as the template reads it there.
export function validateGdprOptions(organizationTermOptions) {
  let emptyRequiredField = false;

  if (Array.isArray(organizationTermOptions?.data)) {
    organizationTermOptions.data.forEach(function (ogt) {
      set(ogt, 'error', false);
      if (ogt.registration && ogt.required && !ogt.selected) {
        emptyRequiredField = true;
        set(ogt, 'error', true);
      }
    });
  }

  return emptyRequiredField;
}

// Trims every attribute of the contact record in place.
export function clearRecordAttributes(me) {
  me.eachAttribute(function (name) {
    set(me, name, clearAttribute(get(me, name)));
  });
}

// Validates the booking contact. Returns the `requiredEmpty` map to publish
// plus whether anything is missing. The address check is intentionally NOT
// here: it needs an awaited relationship and stays in the controller task.
export function validateContact({
  me,
  fieldRequired,
  bookingSettings,
  voucherToUse,
  usaPec,
  isItaliano,
  notRequirePassword,
}) {
  const requiredEmpty = {};
  let emptyRequiredField = false;

  const flag = (key) => {
    requiredEmpty[key] = true;
    emptyRequiredField = true;
  };

  if (!me.fisica_giuridica && !me.last_name) {
    flag('last_name');
  }

  if (!me.fisica_giuridica && !me.first_name) {
    flag('first_name');
  }

  if (bookingSettings?.payment_required && fieldRequired.codice_destinatario && me.fisica_giuridica) {
    if (!usaPec && !me.codice_destinatario) {
      flag('codice_destinatario');
    }
    if (usaPec && !me.pec) {
      flag('pec');
    }
  }

  if (me.fisica_giuridica && !me.ragione_sociale) {
    flag('ragione_sociale');
  }

  if (fieldRequired.data_nascita && !get(me, 'data_nascita')) {
    flag('data_nascita');
  }

  if (fieldRequired.luogo_nascita && !get(me, 'luogo_nascita')) {
    flag('luogo_nascita');
  }

  if (!me.mail || !validateEmail(me.mail)) {
    flag('mail');
  }

  if (!me.mobile || !me.isMobileValid) {
    flag('mobile');
  }

  if (!voucherToUse && !notRequirePassword && !me.id && !me.password) {
    flag('password');
  }

  if (!voucherToUse && fieldRequired.cod_fiscale) {
    if (isItaliano && !get(me, 'cod_fiscale')) {
      flag('cod_fiscale');
    }

    if (bookingSettings?.payment_required && me.fisica_giuridica && !me.piva) {
      flag('piva');
    }
  }

  return { requiredEmpty, emptyRequiredField };
}

// True when the (already resolved) invoice address is missing any mandatory part.
export function isAddressIncomplete(address) {
  return (
    !address.location_address_street ||
    !address.location_address_house ||
    !address.location_address_postal_code ||
    !address.location_address_city ||
    !address.location_address_country
  );
}

// Validates the participant rows. Mutates each person (trimmed attributes and
// its own `requiredEmpty` map) exactly as the original inline loop did, and
// returns the aggregate outcome for the caller.
export function validatePeople({ people, fieldPeopleRequired, productSelected, intl, bookingSettings }) {
  let emptyRequiredField = false;
  let peopleMissing = false;
  const bookErrorArr = [];

  const hidePeople = bookingSettings?.hide_people;
  const showPeople = bookingSettings?.show_people;

  if (!hidePeople && showPeople && (!Array.isArray(people) || people.length === 0)) {
    peopleMissing = true;
    emptyRequiredField = true;
  }

  if (!hidePeople && Array.isArray(people)) {
    people.forEach(function (p) {
      set(p, 'requiredEmpty', {});

      for (var prop in p) {
        p[prop] = clearAttribute(p[prop]);
      }

      if (!p.first_name) {
        set(p, 'requiredEmpty.first_name', true);
        emptyRequiredField = true;
      }

      if (!p.last_name) {
        set(p, 'requiredEmpty.last_name', true);
        emptyRequiredField = true;
      }

      if (fieldPeopleRequired.cod_fiscale && !p.cod_fiscale) {
        set(p, 'requiredEmpty.cod_fiscale', true);
        emptyRequiredField = true;
      }

      if (fieldPeopleRequired.luogo_nascita && !p.luogo_nascita) {
        set(p, 'requiredEmpty.luogo_nascita', true);
        emptyRequiredField = true;
      }

      if (fieldPeopleRequired.mobile && !p.mobile) {
        set(p, 'requiredEmpty.mobile', true);
        emptyRequiredField = true;
      }

      if (fieldPeopleRequired.data_nascita) {
        if (!p.data_nascita) {
          set(p, 'requiredEmpty.data_nascita', true);
          emptyRequiredField = true;
        } else {
          let years = moment().diff(p.data_nascita, 'years');

          if (productSelected && productSelected.min_age && productSelected.min_age > years) {
            set(p, 'requiredEmpty.data_nascita', true);
            emptyRequiredField = true;
            bookErrorArr.push({
              detail: `${intl.t('error min age')}, min ${productSelected.min_age} ${intl.t('years')}`,
            });
          }

          if (productSelected && productSelected.max_age && productSelected.max_age < years) {
            set(p, 'requiredEmpty.data_nascita', true);
            emptyRequiredField = true;
            bookErrorArr.push({
              detail: `${intl.t('error max age')}, max ${productSelected.max_age} ${intl.t('years')}`,
            });
          }
        }
      }
    });
  }

  return { emptyRequiredField, peopleMissing, bookErrorArr };
}
